import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useRecorder } from '../lib/useRecorder';
import { saveRecording } from '../lib/recordingsDb';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const RecorderCtx = createContext(null);
const ACK_KEY = 'jixxeo_recorder_privacy_ack';
const MODE_KEY = 'jixxeo_recorder_pref_mode';

export function RecorderProvider({ children }) {
  const { user, publicSettings } = useAuth();
  const cfg = publicSettings?.recording || {};
  const [showPicker, setShowPicker] = useState(false);
  const [previewRec, setPreviewRec] = useState(null);
  const [quality, setQuality] = useState(cfg.default_quality || 'standard');

  const rec = useRecorder({
    watermarkText: cfg.watermark_text || 'Jixxeo AI',
    watermarkPosition: cfg.watermark_position || 'bottom-right',
    watermarkOpacity: cfg.watermark_opacity ?? 0.7,
    quality,
    maxDurationSec: cfg.max_duration_sec || 1800,
  });

  useEffect(() => { if (rec.errorMsg) toast.error(rec.errorMsg); }, [rec.errorMsg]);

  const bestDefaultMode = useCallback(() => {
    if (rec.capabilities.screen) return 'screen';
    if (rec.capabilities.camera) return 'camera';
    if (rec.capabilities.audio) return 'audio';
    return null;
  }, [rec.capabilities]);

  const beginRecording = useCallback(async (forcedMode = null) => {
    if (!user) { toast.error('Sign in to record your sessions'); return; }
    if (cfg.enabled === false) { toast.error('Recording is disabled by the administrator'); return; }
    if (!rec.anySupported) {
      toast.error('Your browser does not support any form of recording. Try Chrome or Safari, or install Jixxeo AI as an app.');
      return;
    }
    // Always show the picker so the user can choose (and see the privacy note)
    // unless a forcedMode is passed AND they've already ack'd privacy
    const ack = localStorage.getItem(ACK_KEY) === '1';
    if (!forcedMode || !ack) { setShowPicker(true); return; }
    localStorage.setItem(MODE_KEY, forcedMode);
    await rec.start(forcedMode);
  }, [user, cfg.enabled, rec]);

  const chooseModeAndStart = useCallback(async (mode) => {
    localStorage.setItem(ACK_KEY, '1');
    localStorage.setItem(MODE_KEY, mode);
    setShowPicker(false);
    await rec.start(mode);
  }, [rec]);

  const stopAndPreview = useCallback(async () => {
    const result = await rec.stop();
    if (!result) return;
    const id = crypto.randomUUID();
    const created_at = Date.now();
    const label = result.kind === 'audio' ? 'voice note' : 'recording';
    const title = `Jixxeo AI ${label} — ${new Date(created_at).toLocaleString()}`;
    const record = { id, title, created_at, ...result };
    try { await saveRecording(record); } catch { toast.error('Could not save recording locally'); }
    setPreviewRec(record);
  }, [rec]);

  const closePreview = useCallback(() => {
    if (previewRec?.url) { try { URL.revokeObjectURL(previewRec.url); } catch {} }
    setPreviewRec(null);
  }, [previewRec]);

  const value = useMemo(() => ({
    ...rec, quality, setQuality,
    showPicker, setShowPicker,
    previewRec, setPreviewRec, closePreview,
    beginRecording, chooseModeAndStart, stopAndPreview,
    bestDefaultMode,
    settings: cfg,
    enabled: cfg.enabled !== false,
  }), [rec, quality, showPicker, previewRec, closePreview, beginRecording, chooseModeAndStart, stopAndPreview, bestDefaultMode, cfg]);

  return <RecorderCtx.Provider value={value}>{children}</RecorderCtx.Provider>;
}

export const useJixxeoRecorder = () => useContext(RecorderCtx);
